/**
 * AnnouncementsContext - 공지사항 상태 관리
 *
 * 공지사항 목록, 읽음 상태, localStorage 동기화를 제공합니다.
 * 앱 전체에서 공지사항 읽음 상태를 일관성 있게 관리합니다.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import {
  createInitialAnnouncements,
  type Announcement,
  type FilterType,
} from '@/components/announcements';
import { useLanguage } from './AppContext';

// localStorage 키
const READ_ANNOUNCEMENTS_KEY = 'olulo_read_announcements';

// 읽은 공지사항 ID 목록 가져오기
function getReadAnnouncementIds(): Set<string> {
  try {
    const stored = localStorage.getItem(READ_ANNOUNCEMENTS_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch {
    // 의도적으로 에러 무시: localStorage 접근 실패 시 빈 Set 반환 (공지사항 읽음 상태)
  }
  return new Set();
}

// 읽은 공지사항 ID 저장
function saveReadAnnouncementId(id: string): void {
  try {
    const readIds = getReadAnnouncementIds();
    readIds.add(id);
    localStorage.setItem(READ_ANNOUNCEMENTS_KEY, JSON.stringify([...readIds]));
  } catch {
    // 의도적으로 에러 무시: localStorage 저장 실패 시 읽음 상태만 메모리에 유지 (다시 열면 초기화됨)
  }
}

interface AnnouncementsContextValue {
  // 상태
  announcements: Announcement[];
  unreadCount: number;
  selectedFilter: FilterType;
  filteredAnnouncements: Announcement[];

  // 액션
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  setFilter: (filter: FilterType) => void;

  // 헬퍼
  getAnnouncementById: (id: string) => Announcement | null;
  isRead: (id: string) => boolean;
}

const AnnouncementsContext = createContext<AnnouncementsContextValue | null>(null);

interface AnnouncementsProviderProps {
  children: ReactNode;
}

export function AnnouncementsProvider({ children }: AnnouncementsProviderProps) {
  // AppContext에서 로케일 가져오기 (#522: 15 locale 전수 지원)
  const { locale } = useLanguage();

  const [announcements, setAnnouncements] = useState<Announcement[]>(() => {
    // 초기 로드 시 localStorage에서 읽음 상태 복원
    const initial = createInitialAnnouncements(locale);
    const readIds = getReadAnnouncementIds();
    return initial.map((a) => ({
      ...a,
      isRead: readIds.has(a.id) || a.isRead,
    }));
  });

  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all');

  // 언어 변경 시 공지사항 갱신
  useEffect(() => {
    const initial = createInitialAnnouncements(locale);
    const readIds = getReadAnnouncementIds();
    setAnnouncements(initial.map((a) => ({
      ...a,
      isRead: readIds.has(a.id) || a.isRead,
    })));
  }, [locale]);

  // 읽지 않은 공지사항 수
  const unreadCount = useMemo(
    () => announcements.filter((a) => !a.isRead).length,
    [announcements]
  );

  // 필터링된 공지사항
  const filteredAnnouncements = useMemo(() => {
    if (selectedFilter === 'all') return announcements;
    return announcements.filter((a) => a.type === selectedFilter);
  }, [announcements, selectedFilter]);

  // 읽음으로 표시
  const markAsRead = useCallback((id: string) => {
    saveReadAnnouncementId(id);
    setAnnouncements((prev) =>
      prev.map((a) => (a.id === id ? { ...a, isRead: true } : a))
    );
  }, []);

  // 전체 읽음으로 표시
  const markAllAsRead = useCallback(() => {
    // setState 콜백 내에서 현재 상태 기반으로 처리 (레이스 컨디션 방지)
    setAnnouncements((prev) => {
      // localStorage에 저장 (현재 상태 기반)
      prev.forEach((a) => saveReadAnnouncementId(a.id));
      // 상태 업데이트
      return prev.map((a) => ({ ...a, isRead: true }));
    });
  }, []);

  // 필터 변경
  const setFilter = useCallback((filter: FilterType) => {
    setSelectedFilter(filter);
  }, []);

  // ID로 공지사항 찾기
  const getAnnouncementById = useCallback(
    (id: string) => announcements.find((a) => a.id === id) || null,
    [announcements]
  );

  // 읽음 여부 확인
  const isRead = useCallback(
    (id: string) => {
      const announcement = announcements.find((a) => a.id === id);
      return announcement?.isRead ?? false;
    },
    [announcements]
  );

  const value = useMemo<AnnouncementsContextValue>(
    () => ({
      announcements,
      unreadCount,
      selectedFilter,
      filteredAnnouncements,
      markAsRead,
      markAllAsRead,
      setFilter,
      getAnnouncementById,
      isRead,
    }),
    [
      announcements,
      // unreadCount는 announcements에서 파생되므로 제외
      // filteredAnnouncements는 announcements + selectedFilter에서 파생되므로 제외
      selectedFilter,
      markAsRead,
      markAllAsRead,
      setFilter,
      getAnnouncementById,
      isRead,
    ]
  );

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
}

/**
 * useAnnouncements - AnnouncementsContext 사용 훅
 */
export function useAnnouncements(): AnnouncementsContextValue {
  const context = useContext(AnnouncementsContext);
  if (!context) {
    throw new Error('useAnnouncements must be used within an AnnouncementsProvider');
  }
  return context;
}
